/**
 * X Ads — posts created in this browser tab. X's post list can take a long while to show a new
 * promoted-only post (it is there by id at once), so the Creatives page and the campaign wizard
 * put these first until X lists them itself. Kept in sessionStorage so a reload does not lose
 * them; a private window or blocked storage just keeps them in memory.
 */
import type { XPost } from '@/services/x-ads.service';

const KEY = 'xads_recent_posts_v1';
const memory = new Map<string, XPost[]>();

function read(uid: string): XPost[] {
  try {
    const all = JSON.parse(sessionStorage.getItem(KEY) || '{}') as Record<string, XPost[]>;
    return Array.isArray(all[uid]) ? all[uid] : memory.get(uid) ?? [];
  } catch { return memory.get(uid) ?? []; }
}

function write(uid: string, list: XPost[]) {
  memory.set(uid, list);
  try {
    const all = JSON.parse(sessionStorage.getItem(KEY) || '{}') as Record<string, XPost[]>;
    all[uid] = list;
    sessionStorage.setItem(KEY, JSON.stringify(all));
  } catch { /* memory only */ }
}

export function rememberPost(uid: string, post: XPost) {
  if (!post.id) return;
  write(uid, [post, ...read(uid).filter((p) => p.id !== post.id)].slice(0, 50));
}

/** `list` from X with this tab's posts that X does not list yet in front, newest first. */
export function withRecentPosts(uid: string, list: XPost[]): XPost[] {
  const seen = new Set(list.map((p) => p.id));
  const recent = read(uid);
  const missing = recent.filter((p) => !seen.has(p.id));
  if (missing.length !== recent.length) write(uid, missing);
  return [...missing, ...list];
}
